import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";

/**
 * Public preview links for asset versions: anyone with the link sees the final
 * ad on its canvas, nothing else. Only briefed (or later) versions can be shared.
 */

export interface ShareLink {
  id: string;
  /** Relative path, e.g. "/preview/<token>"; prefixed with the app origin. */
  path: string;
  created_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
}

export interface ShareLinkState {
  shareable: boolean;
  status: string;
  share_link: ShareLink | null;
}

interface ShareLinkStore {
  byVersion: Record<string, ShareLinkState>;
  isBusy: boolean;
  fetchShareLink: (versionId: string) => Promise<void>;
  createShareLink: (versionId: string) => Promise<void>;
  revokeShareLink: (versionId: string) => Promise<void>;
}

const message = (error: unknown, fallback: string) =>
  (error as AxiosError<any>).response?.data?.message ?? fallback;

const useShareLinkStore = create<ShareLinkStore>((set) => {
  const request = async (
    versionId: string,
    call: () => Promise<any>,
    fallback: string,
  ) => {
    set({ isBusy: true });
    try {
      const response = await call();
      set((state) => ({
        isBusy: false,
        byVersion: { ...state.byVersion, [versionId]: response.data.data },
      }));
    } catch (error) {
      set({ isBusy: false });
      throw new Error(message(error, fallback));
    }
  };

  return {
    byVersion: {},
    isBusy: false,
    fetchShareLink: (versionId) =>
      request(
        versionId,
        () => apiClient.get(`/asset-versions/${versionId}/share-link`),
        "Failed to load the preview link",
      ),
    createShareLink: (versionId) =>
      request(
        versionId,
        () => apiClient.post(`/asset-versions/${versionId}/share-link`),
        "Failed to create the preview link",
      ),
    revokeShareLink: (versionId) =>
      request(
        versionId,
        () => apiClient.delete(`/asset-versions/${versionId}/share-link`),
        "Failed to turn off the preview link",
      ),
  };
});

export default useShareLinkStore;
